// angular
import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
// components
import { CardsListComponent } from './components/cards-list/cards-list.component';
import { FiltersPanelComponent } from '@app/core/components/filters-panel/filters-panel.component';
import { ProductsFiltersComponent } from './components/products-filters/products-filters.component';
import { MobileSortsSheetComponent } from '@app/core/components/mobile-sorts-sheet/mobile-sorts-sheet.component';
import { ProductsFiltersDialogComponent } from './dialogs/products-filters-dialog/products-filters-dialog.component';
// services
import { DialogService } from '@app/core/shared/services/dialog.service';
import { productSortItems } from '@app/+products/shared/constants/product-sort-items';

@Component({
  selector: 'pwj-products-list',
  standalone: true,
  imports: [
    CardsListComponent,
    FiltersPanelComponent,
    ProductsFiltersComponent,
    MobileSortsSheetComponent,
    TranslateModule,
  ],
  templateUrl: './products-list.component.html',
  styleUrl: './products-list.component.scss',
})
export class ProductsListComponent {
  readonly sortItems = productSortItems;
  readonly filtersSheetOpen = signal(false);
  readonly sortsSheetOpen = signal(false);
  /** show filter button on sort bar when sidebar is hidden (< lg). */
  readonly showFiltersTriggerOnSortBar = signal(true);

  private readonly _dialogService = inject(DialogService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this._platformId)) {
        return;
      }
      const mq = window.matchMedia('(max-width: 65.99em)');
      const sync = () => this.showFiltersTriggerOnSortBar.set(mq.matches);
      sync();
      mq.addEventListener('change', sync);
      this._destroyRef.onDestroy(() => mq.removeEventListener('change', sync));
    });
  }

  onFiltersFromSortBar(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    if (window.matchMedia('(min-width: 48em)').matches) {
      this.openProductsFiltersDialog();
    } else {
      this.openFiltersSheet();
    }
  }

  openFiltersSheet(): void {
    this.sortsSheetOpen.set(false);
    this.filtersSheetOpen.set(true);
  }

  closeFiltersSheet(): void {
    this.filtersSheetOpen.set(false);
  }

  openSortsSheet(): void {
    this.filtersSheetOpen.set(false);
    this.sortsSheetOpen.set(true);
  }

  closeSortsSheet(): void {
    this.sortsSheetOpen.set(false);
  }

  openProductsFiltersDialog(): void {
    this._dialogService.open(ProductsFiltersDialogComponent, {
      /* ~filters rail max-w-352 + padding; avoids wide empty dialog */
      width: 'min(23.5rem, calc(100vw - 1.25rem))',
      maxWidth: 'calc(100vw - 1.25rem)',
      maxHeight: 'min(90vh, 44rem)',
      height: 'auto',
      panelClass: 'overlay-panel--filters-dialog',
    });
  }
}
